import * as React from 'react';
import type { ColorScheme } from 'owa-color-utils/lib/ColorScheme';
import { SurfaceDraggable } from './logic/content/SurfaceDraggable';
import SurfaceResizeBox from './logic/content/SurfaceResizeBox';
import DragDropInfoTip from './surfaceItems/dragDrop/DragDropInfoTip';
import AllDayDragHelper from './surfaceItems/dragDrop/AllDayDragHelper';
import DayWeekDragHelper from './surfaceItems/dragDrop/DayWeekDragHelper';
import type AllowedResizeTypes from './store/schema/AllowedResizeTypes';
import {
    SurfaceItem,
    CalendarSurfaceTransformProps,
    SurfaceDragDropConfig,
    DragOperationType,
    CalendarSurfaceStore,
} from 'owa-calendar-surface-dategrid';

export let allDayDragHelperTemplate: (
    store: CalendarSurfaceStore,
    config: CalendarSurfaceTransformProps,
    allDayRows: SurfaceItem[][],
    calendarFolderId?: string
) => JSX.Element = (store, config, allDayRows, calendarFolderId): JSX.Element => {
    return (
        <AllDayDragHelper
            surfaceStore={store}
            config={config}
            allDayRows={allDayRows}
            splitCalendarFolderId={calendarFolderId}
            key="allDayDragHelper"
        />
    );
};

export let dayWeekDragHelperTemplate: (
    store: CalendarSurfaceStore,
    config: CalendarSurfaceTransformProps,
    calendarFolderId?: string
) => JSX.Element = (store, config, calendarFolderId): JSX.Element => {
    return (
        <DayWeekDragHelper
            surfaceStore={store}
            config={config}
            splitCalendarFolderId={calendarFolderId}
            key="dayWeekDragHelper"
        />
    );
};

/**
 * Makes the given item draggable
 * @param store The surface store
 * @param dragDropConfig darg and drop config
 * @param item The surface item
 * @param element The JSX element for the generated item
 */
export function makeDraggable(
    store: CalendarSurfaceStore,
    dragDropConfig: SurfaceDragDropConfig,
    item: SurfaceItem,
    element: JSX.Element,
    allowedResizeTypes: AllowedResizeTypes
): JSX.Element {
    if (store.enableDragDrop) {
        let draggableKey = item.key + '_drag';
        return (
            <SurfaceDraggable
                item={item}
                store={store}
                dragDropConfig={dragDropConfig}
                dragType={DragOperationType.Drag}
                key={draggableKey}
                allowedResizeTypes={allowedResizeTypes}>
                {element}
            </SurfaceDraggable>
        );
    } else {
        return element;
    }
}

/**
 * Adds surface resize box to the given item
 * @param store The surface store
 * @param dragDropConfig darg and drop config
 * @param item The surface item
 * @param element The JSX element for the generated item
 */
export function addSurfaceBox(
    store: CalendarSurfaceStore,
    item: SurfaceItem,
    element: JSX.Element,
    allowedResizeTypes: AllowedResizeTypes,
    colorScheme: ColorScheme
): JSX.Element {
    if (store.enableDragDrop) {
        return (
            <SurfaceResizeBox
                store={null}
                dragDropConfig={null}
                item={item}
                allowedResizeTypes={allowedResizeTypes}
                colorScheme={colorScheme}>
                {element}
            </SurfaceResizeBox>
        );
    } else {
        return element;
    }
}

export let dragTipTemplate: (store: CalendarSurfaceStore) => JSX.Element = (store): JSX.Element => {
    return <DragDropInfoTip dragDropState={store.dragDropState} />;
};
